import { Cross, LockClosed } from '@cheqroom/icons';
import { Icon, IconButton, Sortable, Stack, Text } from '@cheqroom/ui';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import tw, { styled, theme } from 'twin.macro';

import { SystemField, SystemFieldType as ItemSystemFieldType } from '../../hooks/item-fields/types';
import { Field as ReservationsFieldType } from '../../pages/Reservations/Overview/python.hooks';

type Props = {
	type: 'columns' | 'fields';
	selectedColumns: SystemField<ItemSystemFieldType | ReservationsFieldType | string>[];
	changeColumnOrder: (fromIndex: number, toIndex: number) => void;
	onRemoveColumn: (id: string) => void;
	blockBeforeIndex?: number;
};

const ReorderFields: FC<Props> = ({ type, selectedColumns, changeColumnOrder, onRemoveColumn, blockBeforeIndex }) => {
	const { t } = useTranslation('customize-overview-columns');

	const canReorder = useCallback(
		(fromIndex: number, toIndex: number) => {
			if (blockBeforeIndex !== undefined && toIndex <= blockBeforeIndex) return false;
			return true;
		},
		[blockBeforeIndex]
	);

	return (
		<Stack vertical spacing="loose" tw="w-full">
			<Stack vertical spacing="extraTight">
				<Text weight="bold">
					{t(
						type === 'columns'
							? 'customize_columns.reorder_columns.title'
							: 'customize_item_view.reorder_fields.title'
					)}
				</Text>
				<Text size="small" color="subdued">
					{t(
						type === 'columns'
							? 'customize_columns.reorder_columns.description'
							: 'customize_item_view.reorder_fields.description'
					)}
				</Text>
			</Stack>
			<Sortable onReorder={changeColumnOrder} canReorder={canReorder}>
				<Sortable.Content droppableId="reorder-columns">
					{selectedColumns.map((column, index) => {
						const disabled = !column.removable;

						return (
							<Sortable.Item key={column.id} id={column.id} index={index} disabled={disabled}>
								<StyledField align="center" justify="between" disabled={disabled}>
									<Stack spacing="tight" align="center">
										<Sortable.Handle />
										<Text>{column.name}</Text>
									</Stack>
									{disabled ? (
										<div tw="flex m-1.5 border border-transparent">
											<Icon source={LockClosed} size="small" color={theme`colors.gray.400`} />
										</div>
									) : (
										<IconButton
											source={Cross}
											size="small"
											variation="secondary"
											inverted
											onClick={() => onRemoveColumn(column.id)}
										/>
									)}
								</StyledField>
							</Sortable.Item>
						);
					})}
				</Sortable.Content>
			</Sortable>
		</Stack>
	);
};

export const StyledField = styled(Stack)<{ disabled: boolean }>`
	${tw`bg-white p-1 pl-2 border border-gray-200 rounded-md w-full my-1`}
	${({ disabled }) => disabled && tw`bg-gray-100 text-gray-400`}
`;

export default ReorderFields;
